import { createI18n } from 'vue-i18n';
import { split, head, contains } from 'ramda';
import { localeIds } from './locales';
import { messages as en } from './locales/en';

// Type-define 'en' as the master schema
export type MessageSchema = typeof en;
type Language = 'en' | 'de' | 'es';

// Preload default language
const loadedLanguages: Array<Language> = ['en'];

const i18n = createI18n<[MessageSchema], Language>({
  globalInjection: true,
  warnHtmlInMessage: 'off',
  locale: loadedLanguages[0],
  fallbackLocale: loadedLanguages[0],
  messages: {
    en,
  } as {
    en: MessageSchema;
    de: MessageSchema;
    es: MessageSchema;
  },
  datetimeFormats: {
    en: {
      short: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      },
    },
    de: {
      short: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      },
    },
    es: {
      short: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      },
    },
  },
});

export type I18nPaths = Parameters<(typeof i18n)['global']['t']>[0];

const detected = navigator.languages ? navigator.languages[0] : navigator.language;
const detectedLocale = head(split('-', detected));

/**
 * Find locale:
 * 1. locale from local storage
 * 2. detected locale if allowed
 * 3. en
 *
 * Will be overridden by locale from user.
 */
const locale = (localStorage.getItem('locale') ||
  (contains(detectedLocale, localeIds) ? detectedLocale : loadedLanguages[0])) as Language;

document.documentElement.setAttribute('lang', locale);

export const getLocalLang = () => localStorage.getItem('locale');

export const setLocalLang = (lang: Language) => {
  localStorage.setItem('locale', lang);

  return lang;
};

const setI18nLanguage = (lang: Language) => {
  i18n.global.locale = lang;
  document.documentElement.setAttribute('locale', lang);
  setLocalLang(lang);

  return lang;
};

export const getI18nLanguage = () =>
  localStorage.getItem('locale') || (contains(detectedLocale, localeIds) ? detectedLocale : 'en');

// eslint-disable-next-line @typescript-eslint/init-declarations
let loadId: symbol;

export const loadLanguageAsync = async (lang: Language) => {
  const localLoadId = Symbol('load');
  loadId = localLoadId;

  // If the same language
  if (i18n.global.locale === lang) {
    return Promise.resolve(setI18nLanguage(lang));
  }

  // If the language was already loaded
  if (loadedLanguages.includes(lang)) {
    return Promise.resolve(setI18nLanguage(lang));
  }

  // If the language hasn't been loaded yet
  const messageModule = await import(/* webpackChunkName: "lang-[request]" */ `./locales/${lang}.ts`);
  // Make sure we don't overwrite a load that came later. An already loaded language can be set faster than one that needs to be loaded from server
  if (localLoadId === loadId) {
    i18n.global.setLocaleMessage(lang, messageModule.messages);
    loadedLanguages.push(lang);

    return setI18nLanguage(lang);
  }

  return i18n.global.locale;
};

// Set the detected language
loadLanguageAsync(locale);

export default i18n;
